import type {ReactElement} from 'react';
import {useRef} from 'react';
import React from 'react';
import {Button, Size, Type} from '@Components/button';
import {addNewPage} from '@Components/poster-editor/library/poster-editor-page-library';
import styles from './add-new-page-button.module.scss';
import {useGetNumberOfPages} from '@Hooks/poster-editor/useGetNumberOfPages';
import {Tooltip} from '@Components/tooltip';
import {Text, TextSize} from '@Components/text';
import {useAppSelector} from '@/hooks';
import {useShowContextualSkeletons} from '@Hooks/poster-editor/useShowContextualSkeletons';
import {AddPageGA4EventParams, GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {isEditorMobileVariant} from '@Components/poster-editor/library/is-editor-mobile-variant';
import {MAX_NUMBER_OF_PAGES} from '@PosterWhiteboard/poster/poster.class';
import {PosterEditorMultipagePopup} from '@Components/poster-editor/components/poster-editor-multipage-popup/poster-editor-multipage-popup';

export function AddNewPageButton(): ReactElement | null {
  const numPages = useGetNumberOfPages();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [showTooltip, setShowTooltip] = React.useState(false);
  const isMultiPageEnabled = window.PMW.isMultipageAvailable;
  const isTimelineModeActive = useAppSelector((state) => {
    return state.posterEditor.isTimelineModeActive;
  });
  const isVersionHistoryModeActive = useAppSelector((state) => {
    return state.posterEditor.isVersionHistoryModeActive;
  });
  const isPosterLoading = useShowContextualSkeletons();

  if (isTimelineModeActive || isVersionHistoryModeActive || !isMultiPageEnabled || isPosterLoading || isEditorMobileVariant()) {
    return null;
  }

  function onClick(): void {
    trackPosterBuilderGA4Events(GA4EventName.ADD_PAGE, {
      [GA4EventParamName.TYPE]: AddPageGA4EventParams.PAGE_BOTTOM_BUTTON,
    });
    addNewPage().catch((e: unknown) => {
      console.error(e);
    });
  }

  const onMouseEnter = (): void => {
    if (numPages < MAX_NUMBER_OF_PAGES) {
      return;
    }

    setShowTooltip(true);
  };
  const onMouseLeave = (): void => {
    if (showTooltip) {
      setShowTooltip(false);
    }
  };

  return (
    <div className={`${styles.container} flex-center`} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} ref={containerRef}>
      {showTooltip ? (
        <Tooltip className={styles.tooltip} id="max-limit-tooltip" text={<Text size={TextSize.XSMALL} val={window.i18next.t('pmwjs_max_page_limit_message')} />} />
      ) : null}
      <div className={styles.buttonWrapper}>
        {isEditorMobileVariant() ? null : <PosterEditorMultipagePopup />}
        <Button
          disabled={numPages >= MAX_NUMBER_OF_PAGES}
          onClick={onClick}
          icon="icon-new-page"
          type={Type.SECONDARY}
          size={Size.XSMALL}
          customClasses={styles.button}
          text={window.i18next.t('pmwjs_add_page')}
        />
      </div>
    </div>
  );
}
